import { Component, EventEmitter, OnDestroy, OnInit, Output, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import * as _ from 'lodash-es';
import { MarkAsReviewedService } from './mark-as-reviewed.service';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  gridPayloadFromActiveScreen,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-mark-as-reviewed',
    templateUrl: './mark-as-reviewed.component.html',
    styleUrls: ['./mark-as-reviewed.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class MarkAsReviewedComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();
  private readonly subs = new Subscription();

  filterParams: any = {};
  baseURLs: any = {'admin':'','api':'','custom':'','efile':'','pdfEngine':'','bulkPdfEngine':''};
  rowData: any = {};
  gridData: any = null;

  // Modal configuration - following AngularJS pattern
  modalConfig: ConfirmationModalConfig = {
    title: 'Confirm',
    message: 'Are you sure you want to Mark selected partnerships as Reviewed?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast',
    loading: false,
    loadingText: 'Processing...'
  };

  // Component properties following fir163jsync pattern
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};
  
  showModal: boolean = true;
  markPshipsReviewedDtls: any[] = [];
  logged_in_user: string = '';
  modalName: string = '';
  tax_year: string = '';
  scenario: string = '';
  jcd_key: string = '';
  markAsReviewedLimit: string = '';

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private gtwSnackbarService: GtwSnackbarService,
    private markAsReviewedService: MarkAsReviewedService
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = header.globalParams || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.gridData = gridPayloadFromActiveScreen(activeScreen);
  }

  init(): void {
    // Following fir163jsync pattern - check if all required data is available
    if (this.baseURLs && Object.keys(this.baseURLs).length > 0 && 
        this.filterParams && Object.keys(this.filterParams).length > 0 &&
        this.GENERAL_CONFIG && Object.keys(this.GENERAL_CONFIG).length > 0) {
      this.initializeData();
    }
  }

  initializeData(): void {
    // Get logged in user from USER_SETTINGS like fir163jsync
    if (this.USER_SETTINGS?.user?.sso_id) {
      this.logged_in_user = this.USER_SETTINGS.user.sso_id;
    }
    
    // Get modal name from globalService or filterParams
    if (this.globalService?.modalScreen?.modal_name) {
      this.modalName = this.globalService.modalScreen.modal_name;
    }

    // Get tax year, scenario, and jcd_key from filterParams (real data, not hardcoded)
    this.tax_year = this.filterParams?.tax_year || this.rowData?.TAX_YEAR || '';
    this.scenario = this.filterParams?.scenario || this.rowData?.SCENARIO || '';
    this.jcd_key = this.filterParams?.jcd_key || this.rowData?.JCD_KEY || this.globalService?.globalParams?.jcd_key || '';
    
    // Process grid data if rowData is available
    if (this.rowData) {
      this.processGridData();
    }

    // Fetch max count entities like AngularJS
    this.fetchMaxCountEntities();
  }

  processGridData(): void {
    this.markPshipsReviewedDtls = [];

    const selections = this.resolveSelectedRows();
    if (!selections || !selections.length) {
      return;
    }

    for (const item of selections) {
      const row = item?.data ?? item;
      if (row?.LE_KEY != null) {
        this.markPshipsReviewedDtls.push({ le_key: row.LE_KEY });
      }
    }
  }

  private resolveSelectedRows(): any[] {
    if (Array.isArray(this.gridData?.batchSelect) && this.gridData.batchSelect.length) {
      return this.gridData.batchSelect;
    }
    if (Array.isArray(this.gridData?.gridData)) {
      return this.gridData.gridData;
    }
    if (Array.isArray(this.gridData)) {
      return this.gridData;
    }
    if (Array.isArray(this.rowData)) {
      return this.rowData;
    }
    if (this.rowData && typeof this.rowData === 'object' && this.rowData.LE_KEY != null) {
      return [this.rowData];
    }
    return [];
  }

  fetchMaxCountEntities(): void {
    // Fetch max count entities exactly like AngularJS
    const params = { 
      "action_code": 'mysfkb', 
      "screen_key": this.globalService?.globalParams?.screen_key || this.filterParams?.screen_key || ''
    };
    
    this.markAsReviewedService.fetchMaxCountEntities(
      this.GENERAL_CONFIG.base_url,
      params
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data.jsonObject && data.jsonObject.length > 0) {
          this.markAsReviewedLimit = data.jsonObject[0].ATTRIB_VALUE;
        }
      },
      (error: any) => {
        console.error('Error fetching max count entities:', error);
      }
    );
  }

  onConfirm(): void {
    this.confirmMarkForReviewed();
  }

  cancel(): void {
    // Following fir163jsync pattern - dispatch CustomEvent to close modal
    this.showModal = false;
    
    const updateEvent = new CustomEvent('request-grid-update', {
      detail: 'cancel',
      bubbles: true
    });
    
    if (document.querySelector('gtw-mark-as-reviewed-ce')) {
      document.querySelector('gtw-mark-as-reviewed-ce')?.dispatchEvent(updateEvent);
    }
  }
  
  onCancel(): void {
    // Called from confirmation modal - delegates to cancel()
    this.cancel();
  }

  confirmMarkForReviewed(): void {
    // Follow exact AngularJS behavior with validation
    if(this.markPshipsReviewedDtls.length > parseInt(this.markAsReviewedLimit)){
      this.gtwSnackbarService.openSnackBar("Can't select more than " + this.markAsReviewedLimit + " entities", 'gtw-snackbar--red');
      return;
    }

    // Follow exact AngularJS behavior
    const returnClobSettingsObj: any = {};
    
    // Build settings object with real data (NOT hardcoded)
    returnClobSettingsObj['tax_year'] = this.tax_year;
    returnClobSettingsObj['scenario'] = this.scenario;
    returnClobSettingsObj['jcd_key'] = this.jcd_key;
    returnClobSettingsObj['sso_id'] = this.logged_in_user;
    
    const ecSaveDtls = this.markPshipsReviewedDtls;
    
    if(this.markPshipsReviewedDtls != undefined && this.markPshipsReviewedDtls.length > 0) {
      const message = "Submitted Selected Partnership(s) to Marked as Reviewed";
      
      // Call sendDetails function like AngularJS
      this.sendDetails(returnClobSettingsObj, ecSaveDtls, message);
    }
  }

  sendDetails(returnClobSettingsObj: any, ecSaveDtls: any[], message: string): void {
    this.modalConfig.loading = true;

    this.markAsReviewedService.markAsReviewed(
      this.GENERAL_CONFIG.base_url,
      returnClobSettingsObj,
      ecSaveDtls,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        this.modalConfig.loading = false;
        if (result.errorMessage && result.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(result.errorMessage, 'gtw-snackbar--red');
        } else {
          this.gtwSnackbarService.openSnackBar(message, 'gtw-snackbar--green');
          this.cancel();
          setTimeout(() => {
            const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen;
            if (activeScreen?.screen_key) {
              this.gtwWorkspace.refreshGridData.next(activeScreen);
            }
          }, 500);
        }
      },
      (error: any) => {
        this.modalConfig.loading = false;
        console.error('Mark as reviewed error:', error);
        this.gtwSnackbarService.openSnackBar('Error occurred while marking partnerships as reviewed. Please try again.', 'gtw-snackbar--red');
      }
    );
  }
}
